<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import store from "@/store";
import moment from "moment";

import { toast } from "@/common/utils/toast";
import { maskCurrency } from "@/common/utils/mask";

import {
  invoiceList,
  invoiceDel,
  invoiceChart,
} from "@/common/api/rest/invoice";

import type { InvoiceType } from "@/common/types/invoice";

import { PanelTheme } from "@/common/themes";

import {
  baseConfirm,
  baseDate,
  baseSelect,
  baseModal,
  baseTable,
  tabMenu,
} from "@/common/components";

import { ChartBar } from "@/components";

const { t, tm } = useI18n();

const route = useRoute();

type TabActiveType = "PENDING" | "HISTORY" | "REPORT" | "CHARTS";

const tabList = computed(() => [
  {
    key: "PENDING",
    label: t("invoice.labels.pending").toUpperCase(),
  },
  {
    key: "HISTORY",
    label: t("invoice.labels.history").toUpperCase(),
  },
  //   {
  //     key: "REPORT",
  //     label: t("common.labels.report").toUpperCase(),
  //   },
  {
    key: "CHARTS",
    label: t("common.labels.charts").toUpperCase(),
  },
]);

const tableHeader = computed(() => [
  { key: "date", label: t("invoice.labels.date") },
  { key: "member", label: t("invoice.labels.member") },
  { key: "product_type", label: t("invoice.labels.product") },
  { key: "product", label: t("invoice.labels.name") },
  { key: "price", label: t("invoice.labels.value") },
  { key: "status", label: t("invoice.labels.status") },
]);

const typeOptions = computed(
  () => tm("invoice.enum.product_type") as Record<string, string>
);

const statusEnum = computed(
  () => tm("invoice.enum.status") as Record<string, string>
);

const currencyEnum = computed(
  () => tm("invoice.enum.currency") as Record<string, string>
);

const statusOptions = computed(() => [
  {
    option: t("invoice.labels.all"),
    value: "ALL",
  },
  {
    option: t("invoice.labels.pending"),
    value: "PENDING",
  },
  {
    option: t("invoice.labels.paid"),
    value: "PAID",
  },
  {
    option: t("invoice.labels.canceled"),
    value: "CANCELLED",
  },
  {
    option: t("invoice.labels.refunded"),
    value: "REFUNDED",
  },
]);

const monthsOptions = computed(() => {
  const months = Object.entries(
    tm("date.months.full") as Record<string, string>
  ).map(([_, option], index) => ({
    option,
    value: index + 1,
  }));

  return [
    {
      option: t("invoice.labels.all"),
      value: "all",
    },
    ...months,
  ];
});

const monthsList = computed(() =>
  Object.values(tm("date.months.full") as Record<string, string>)
);

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const yearsBefore = Array.from({ length: 11 }, (_, index) => index)
    .filter((_, index) => index > 0)
    .map((index) => currentYear - index)
    .sort((a, b) => a - b);

  const yearsAfter = Array.from(
    { length: 11 },
    (_, index) => currentYear + index
  );

  return yearsBefore.concat(yearsAfter).map((year) => ({
    option: `${year}`,
    value: `${year}`,
  }));
});

const tabActive = ref<TabActiveType>("PENDING");

const tableData = ref<InvoiceType[]>([]);
const selected = ref<InvoiceType | null>(null);
const selectedStatus = ref<string>("PENDING");

const historyStartDate = ref("");
const historyEndDate = ref("");

const chartData = ref([]);
const chartLabel = ref([]);
const chartMonth = ref("all");
const chartYear = ref();
const chartKey = ref(0);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(false);
const showConfirmDel = ref(false);

function fetchData() {
  loading.value = true;

  invoiceList({
    search: search.value,
    page: page.value,
    limit: limit.value,
    status: selectedStatus.value,
    start_date: historyStartDate.value,
    end_date: historyEndDate.value,
    order: "DESC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

function searchItem(input: string) {
  search.value = input;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function selectTab(tab: TabActiveType) {
  if (tabActive.value === tab) return;

  tabActive.value = tab;

  if (tab === "PENDING") {
    selectedStatus.value = "PENDING";
    historyStartDate.value = "";
    historyEndDate.value = "";
    fetchData();
  }

  if (tab === "HISTORY") {
    historyStartDate.value = moment().subtract(30, "days").format("YYYY-MM-DD");
    historyEndDate.value = moment().format("YYYY-MM-DD");
    selectedStatus.value = "ALL";
    fetchData();
  }

  if (tab === "CHARTS") {
    chartYear.value = String(moment().year());
    fetchChartData();
  }
}

function remove(data: InvoiceType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  showConfirmDel.value = false;
  store.commit("SET_LOADING", true);

  invoiceDel(selected.value.invoice_id)
    .then((response) => {
      const { success, message } = response;
      fetchData();
      removeCancel();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeStatusType() {
  fetchData();
}

function changeHistoryDate() {
  fetchData();
}

function showInvoice(data: InvoiceType) {
  window.open(data.invoice_url, "_blank");
}

// CHARTS
function resumeChart(data: any) {
  chartLabel.value = [];
  chartData.value = [];

  if (data?.length === 0) {
    chartKey.value++;
    return;
  }

  const labels = [];
  const datasets = [
    {
      label: "Pendente",
      data: [],
      backgroundColor: "#a1a1a1",
    },
    {
      label: "Pago",
      data: [],
      backgroundColor: "#003b23",
    },
    {
      label: "Cancelado",
      data: [],
      backgroundColor: "#df9800",
    },
    {
      label: "Extornado",
      data: [],
      backgroundColor: "#fd1a1e",
    },
  ];

  Object.entries(data).forEach(([key, value]) => {
    const month = Number(key) - 1;

    const infos = (value as any).infos;
    const pendingAmount = infos?.pending?.amount;
    const paidAmount = infos?.paid?.amount;
    const canceledAmount = infos?.canceled?.amount;
    const refundedAmount = infos?.refunded?.amount;

    labels.push(monthsList.value[month]);

    datasets[0].data.push(pendingAmount);
    datasets[1].data.push(paidAmount);
    datasets[2].data.push(canceledAmount);
    datasets[3].data.push(refundedAmount);
  });

  chartLabel.value = labels;
  chartData.value = datasets;
  chartKey.value++;
}

function fetchChartData() {
  invoiceChart(chartMonth.value, chartYear.value)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        resumeChart(data.list);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <panel-theme>
    <tab-menu
      :tabs="tabList"
      :active="tabActive"
      :tooltip="false"
      width="fit-content"
      padding-inline="1.5rem"
      @select:tab="selectTab"
    >
      <template #content>
        <template v-if="tabActive === 'PENDING'">
          <baseTable
            :header="tableHeader"
            :data="tableData"
            :limit="limit"
            :page="page"
            :pages="pages"
            :total="total"
            :searched="searched"
            :loading="loading"
            :options="true"
            :view="true"
            :del="true"
            :edit="false"
            :add="false"
            @change:page="changePage"
            @searching="searchItem"
            @btn:view="showInvoice"
            @btn:del="remove"
          >
            <template #status="{ data }">
              {{ statusEnum[data.status] }}
            </template>

            <template #product="{ data }">
              <div class="w-max-content">
                {{ data.product?.name }}
              </div>
            </template>

            <template #date="{ data }">
              <div class="w-max-content">
                {{ moment(data.created).format("DD/MM/YYYY") }}
              </div>
            </template>

            <template #price="{ data }">
              {{ currencyEnum[data.currency] }}
              {{ maskCurrency(data.price, data.currency) }}
            </template>

            <template #product_type="{ data }">
              {{ typeOptions[data.product_type] }}
            </template>

            <template #member="{ data }">
              {{ data.member?.first_name }}
            </template>
          </baseTable>
        </template>

        <template v-if="tabActive === 'HISTORY'">
          <div class="tab-history">
            <div class="header">
              <div class="flex-1">
                <base-select
                  v-model="selectedStatus"
                  :value="selectedStatus"
                  :options="statusOptions"
                  :is-empty="false"
                  @change="changeStatusType()"
                >
                  <template #label> {{ t("invoice.labels.type") }}</template>
                </base-select>
              </div>

              <div class="flex-1">
                <base-date
                  v-model="historyStartDate"
                  :value="historyStartDate"
                  value-mask="YYYY-MM-DD"
                  placeholder="dd/mm/yyyy"
                  @update:modelValue="changeHistoryDate()"
                >
                  <template #label>
                    {{ t("invoice.labels.start_date") }} *
                  </template>
                </base-date>
              </div>

              <div class="flex-1">
                <base-date
                  v-model="historyEndDate"
                  :value="historyEndDate"
                  value-mask="YYYY-MM-DD"
                  placeholder="dd/mm/yyyy"
                  @update:modelValue="changeHistoryDate()"
                >
                  <template #label>
                    {{ t("invoice.labels.end_date") }} *
                  </template>
                </base-date>
              </div>
            </div>

            <div class="content">
              <baseTable
                :header="tableHeader"
                :data="tableData"
                :limit="limit"
                :page="page"
                :pages="pages"
                :total="total"
                :searched="searched"
                :loading="loading"
                :options="true"
                :view="true"
                :del="true"
                :edit="false"
                :add="false"
                @change:page="changePage"
                @searching="searchItem"
                @btn:view="showInvoice"
                @btn:del="remove"
              >
                <template #status="{ data }">
                  {{ statusEnum[data.status] }}
                </template>

                <template #product="{ data }">
                  <div class="w-max-content">
                    {{ data.product?.name }}
                  </div>
                </template>

                <template #date="{ data }">
                  <div class="w-max-content">
                    {{ moment(data.created).format("DD/MM/YYYY") }}
                  </div>
                </template>

                <template #price="{ data }">
                  {{ currencyEnum[data.currency] }}
                  {{ maskCurrency(data.price, data.currency) }}
                </template>

                <template #product_type="{ data }">
                  {{ typeOptions[data.product_type] }}
                </template>

                <template #member="{ data }">
                  {{ data.member?.first_name }}
                </template>
              </baseTable>
            </div>
          </div>
        </template>

        <template v-if="tabActive === 'CHARTS'">
          <div class="tab-charts">
            <div class="header">
              <div class="flex-1">
                <base-select
                  v-model="chartMonth"
                  :value="chartMonth"
                  :options="monthsOptions"
                  :is-empty="false"
                  @change="fetchChartData()"
                >
                  <template #label>
                    {{ t("date.labels.month") }}
                  </template>
                </base-select>
              </div>

              <div class="flex-1">
                <base-select
                  v-model="chartYear"
                  :value="chartYear"
                  :options="yearOptions"
                  :is-empty="false"
                  @change="fetchChartData()"
                >
                  <template #label>
                    {{ t("date.labels.year") }}
                  </template>
                </base-select>
              </div>
            </div>

            <div class="content">
              <div class="flex flex-col">
                <p>MOVIMENTAÇÃO FINANCEIRA</p>
                <chart-bar
                  :key="chartKey"
                  :labels="chartLabel"
                  :data="chartData"
                />
              </div>
            </div>
          </div>
        </template>
      </template>
    </tab-menu>

    <base-confirm
      :show="showConfirmDel"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.finance'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: selected?.product?.name,
        })
      "
      @cancel="removeCancel"
      @confirm="removeConfirm"
    />
  </panel-theme>
</template>

<style lang="scss" scoped>
.tab-history {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .header {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    table {
      width: 100%;
      border-collapse: collapse;

      th {
        padding: 0;
        border: 1px solid #ccc;

        td {
          border: none;
          border-right: 1px solid #ccc;
        }
      }

      td {
        padding: 0.5rem;
        border: 1px solid #ccc;
      }
    }
  }
}

.tab-charts {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .header {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }
}
</style>
